import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, RefreshCw, CheckCircle2, Circle, Search, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function AdminSchoolArticles() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState(null); // school_id currently regenerating

  const load = async () => {
    const { data } = await api.get(`${V1}/admin/schools/articles/status`);
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const bulkGenerate = async () => {
    if (!window.confirm("Tüm yayındaki okullar için 4 SEO makalesi arka planda üretilsin mi? Bu birkaç dakika sürebilir.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${V1}/admin/schools/articles/bulk-generate`);
      toast.success(`${data.queued} okul için ${data.year} yılı makale üretimi başlatıldı`);
      setTimeout(load, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  const regenSchool = async (sid, sname) => {
    if (!window.confirm(`${sname} için mevcut ${data?.year} yılı 4 makale silinip yeniden üretilecek. Onaylıyor musunuz?`)) return;
    setRegen(sid);
    try {
      await api.post(`${V1}/admin/schools/${sid}/articles/regenerate`);
      toast.success(`${sname} — yeniden üretim başlatıldı`);
      setTimeout(load, 5000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setRegen(null); }
  };

  if (!data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const filtered = data.schools.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.province || "").toLowerCase().includes(q.toLowerCase()));
  const doneCount = data.schools.filter((s) => s.complete).length;

  return (
    <div className="space-y-6" data-testid="admin-articles-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">İçerik Üretimi</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> Okul SEO Makaleleri
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Her okul için otomatik 4 SEO makalesi: Erken Kayıt, Bursluluk, Yaz Okulu, {data.year} Kayıt Dönemi.
          </p>
        </div>
        <Button onClick={bulkGenerate} disabled={busy} size="lg" data-testid="bulk-generate-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Tüm Okullara Toplu Üret
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Metric label="Yıl" value={data.year} />
        <Metric label="Toplam Okul" value={data.schools.length} />
        <Metric label="Tamamlanan" value={doneCount} accent />
        <Metric label="Üretilen Makale" value={`${data.total_articles} / ${data.expected_total}`} />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Okul veya il ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="article-school-search" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Okul</th>
              <th className="text-left p-3">Konum</th>
              <th className="text-left p-3">İlerleme</th>
              <th className="text-left p-3">Durum</th>
              <th className="text-right p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Okul bulunamadı</td></tr>}
            {filtered.map((s) => (
              <tr key={s.school_id} className="border-t border-border" data-testid={`article-school-${s.school_id}`}>
                <td className="p-3 font-medium">{s.name}</td>
                <td className="p-3 text-muted-foreground">{s.province} / {s.district}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-muted rounded overflow-hidden">
                      <div className={`h-full ${s.complete ? "bg-success" : "bg-accent"}`} style={{ width: `${(s.count / s.total) * 100}%` }} />
                    </div>
                    <span className="text-xs font-mono">{s.count}/{s.total}</span>
                  </div>
                </td>
                <td className="p-3">
                  {s.complete ? (
                    <span className="inline-flex items-center gap-1 text-success text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Tamam</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground text-xs"><Circle className="h-3.5 w-3.5" /> Bekliyor</span>
                  )}
                </td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  <Button size="sm" variant="ghost" asChild data-testid={`view-school-${s.school_id}`}>
                    <Link to={`/okul/${s.slug}`} target="_blank"><ExternalLink className="h-3.5 w-3.5" /></Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => regenSchool(s.school_id, s.name)} disabled={regen === s.school_id} data-testid={`regen-school-${s.school_id}`}>
                    {regen === s.school_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Yenile
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Metric = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl border ${accent ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-3xl font-display font-bold mt-1">{value}</div>
  </div>
);
